import React, { useState } from 'react';
import { Form, Input, Button, message, Typography, Checkbox } from 'antd';
import { UserOutlined, LockOutlined, MailOutlined, PhoneOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import authService from '../../services/authService';
import './Register.css';

const { Title } = Typography;

// Password validation function
const validatePassword = (password) => {
  const hasMinLength = password.length >= 8;
  const hasCapitalLetter = /[A-Z]/.test(password);
  const hasNumber = /[0-9]/.test(password);
  const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);
  
  if (!hasMinLength) return 'Password must be at least 8 characters long';
  if (!hasCapitalLetter) return 'Password must contain at least one capital letter';
  if (!hasNumber) return 'Password must contain at least one number (0-9)';
  if (!hasSpecialChar) return 'Password must contain at least one special character';
  
  return null;
};

// Simple CAPTCHA validation
const validateCaptcha = (captcha, userInput) => {
  return captcha.toLowerCase() === userInput.toLowerCase();
};

const Register = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [captcha, setCaptcha] = useState('');
  const [captchaInput, setCaptchaInput] = useState('');

  // Generate CAPTCHA on component mount
  React.useEffect(() => {
    generateCaptcha();
  }, []);

  const generateCaptcha = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 6; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setCaptcha(result);
  };

    const onFinish = async (values) => {
    // Validate CAPTCHA
    if (!validateCaptcha(captcha, captchaInput)) {
      message.error('CAPTCHA verification failed. Please try again.');
      generateCaptcha();
      setCaptchaInput('');
      return;
    }

    setLoading(true);
    try {
      await authService.register(values);
      message.success('Registration successful! Your account is pending approval.');
      
      // Show approval pending message with contact info
      message.info({
        content: (
          <div style={{ padding: '10px' }}>
            <p><strong>Account Pending Approval</strong></p>
            <p>Your account has been created and is pending admin approval.</p>
            <p>For faster approval, contact us:</p>
            <div style={{ marginTop: '10px' }}>
              <a 
                href="tel:00923281419656" 
                style={{ 
                  display: 'inline-block', 
                  marginRight: '10px', 
                  padding: '5px 10px', 
                  background: '#1890ff', 
                  color: 'white', 
                  textDecoration: 'none', 
                  borderRadius: '4px' 
                }}
              >
                📞 Call Us
              </a>
              <a 
                href={`https://wa.me/00923281419656?text=I%20want%20to%20approve%20my%20account%20-%20Name:%20${encodeURIComponent(values.first_name + ' ' + values.last_name)}%20Phone:%20${encodeURIComponent(values.phone_number)}%20Email:%20${encodeURIComponent(values.email)}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ 
                  display: 'inline-block', 
                  padding: '5px 10px', 
                  background: '#25D366', 
                  color: 'white', 
                  textDecoration: 'none', 
                  borderRadius: '4px' 
                }}
              >
                💬 WhatsApp
              </a>
            </div>
          </div>
        ),
        duration: 10,
      });
      
      navigate('/login');
    } catch (error) {
      if (error.response?.data?.message?.includes('email')) {
        message.error('This email is already registered. Please use a different email or login.');
      } else {
        message.error('Registration failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="register-container">
      <div className="register-content">
        {/* MaveshiBids Logo */}
        <div className="logo-section" style={{ textAlign: 'center', marginBottom: '30px' }}>
          <img 
            src="/assets/images/logo.png" 
            alt="MaveshiBids" 
            style={{ 
              height: '60px', 
              cursor: 'pointer',
              transition: 'transform 0.2s ease'
            }}
            onClick={() => navigate('/')}
            onMouseOver={(e) => e.target.style.transform = 'scale(1.05)'}
            onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
          />
        
        </div>

        <div className="register-header">
          {/* <h1>Create Account</h1> */}
          <p>Join us and create your account</p>
        </div>
        <Form
          name="register"
          onFinish={onFinish}
          layout="vertical"
          className="register-form"
        >
          <div className="name-fields">
            <Form.Item
              name="first_name"
              label="First Name"
              rules={[{ required: true, message: 'Please input your first name!' }]}
            >
              <Input
                prefix={<UserOutlined className="input-icon" />}
                placeholder="Enter your first name"
                size="large"
              />
            </Form.Item>
            <Form.Item
              name="last_name"
              label="Last Name"
              rules={[{ required: true, message: 'Please input your last name!' }]}
            >
              <Input
                prefix={<UserOutlined className="input-icon" />}
                placeholder="Enter your last name"
                size="large"
              />
            </Form.Item>
          </div>
          <Form.Item
            name="email"
            label="Email Address"
            rules={[
              { required: true, message: 'Please input your email!' },
              { type: 'email', message: 'Please enter a valid email!' },
            ]}
          >
            <Input
              prefix={<MailOutlined className="input-icon" />}
              placeholder="Enter your email"
              size="large"
            />
          </Form.Item>
          <Form.Item
            name="phone_number"
            label="Phone Number"
            rules={[
              { required: true, message: 'Please input your phone number!' },
              { max: 20, message: 'Phone number must be less than 20 characters!' },
              { pattern: /^[\+]?[0-9\s\-\(\)]*$/, message: 'Please enter a valid phone number!' },
            ]}
          >
            <Input
              prefix={<PhoneOutlined className="input-icon" />}
              placeholder="Enter your phone number"
              size="large"
            />
          </Form.Item>
          <Form.Item
            name="password"
            label="Password"
            rules={[
              { required: true, message: 'Please input your password!' },
              { min: 8, message: 'Password must be at least 8 characters!' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value) return Promise.resolve();
                  const error = validatePassword(value);
                  if (error) {
                    return Promise.reject(new Error(error));
                  }
                  return Promise.resolve();
                },
              }),
            ]}
            extra="Password must be at least 8 characters long, contain one capital letter, one number, and one special character"
          >
            <Input.Password
              prefix={<LockOutlined className="input-icon" />}
              placeholder="Create a password"
              size="large"
            />
          </Form.Item>
          <Form.Item
            name="password_confirmation"
            label="Confirm Password"
            dependencies={['password']}
            rules={[
              { required: true, message: 'Please confirm your password!' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Passwords do not match!'));
                },
              }),
            ]}
          >
            <Input.Password
              prefix={<LockOutlined className="input-icon" />}
              placeholder="Confirm your password"
              size="large"
            />
          </Form.Item>

          {/* CAPTCHA Section */}
          <Form.Item
            label="CAPTCHA Verification"
            required
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
              <div 
                style={{
                  background: 'linear-gradient(45deg, #f0f0f0, #e0e0e0)',
                  padding: '10px 15px',
                  borderRadius: '4px',
                  fontFamily: 'monospace',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  letterSpacing: '2px',
                  border: '2px solid #d9d9d9',
                  userSelect: 'none',
                  minWidth: '120px',
                  textAlign: 'center'
                }}
              >
                {captcha}
              </div>
              <Button 
                type="text" 
                onClick={generateCaptcha}
                style={{ padding: '4px 8px' }}
              >
                🔄
              </Button>
            </div>
            <Input
              placeholder="Enter the code above"
              value={captchaInput}
              onChange={(e) => setCaptchaInput(e.target.value)}
              size="large"
              maxLength={6}
            />
          </Form.Item>

          {/* Terms and Conditions */}
          <Form.Item
            name="terms"
            valuePropName="checked"
            rules={[
              {
                validator: (_, value) =>
                  value ? Promise.resolve() : Promise.reject(new Error('You must accept the Terms and Conditions')),
              },
            ]}
          >
            <Checkbox>
              I agree to the{' '}
              <Button 
                type="link" 
                onClick={() => navigate('/terms-and-conditions')}
                style={{ padding: 0, height: 'auto' }}
              >
                Terms and Conditions
              </Button>
              {' '}and{' '}
              <Button 
                type="link" 
                onClick={() => navigate('/privacy-policy')}
                style={{ padding: 0, height: 'auto' }}
              >
                Privacy Policy
              </Button>
            </Checkbox>
          </Form.Item>

          <Form.Item className="form-actions">
            <Button
              type="primary"
              htmlType="submit"
              block
              size="large"
              loading={loading}
            >
              Create Account
            </Button>
          </Form.Item>
          <div className="form-footer">
            <p>Already have an account?</p>
            <Button
              type="link"
              onClick={() => navigate('/login')}
              className="login-link"
            >
              Sign in instead
            </Button>
          </div>
        </Form>
      </div>
    </div>
  );
};

export default Register;